import { Component } from '@theme/component';

/**
 * Lightweight tooltip web component.
 *
 * Expected markup:
 * - trigger element with `ref="trigger"`
 * - tooltip panel with `ref="panel"`
 *
 * @typedef {object} Refs
 * @property {HTMLElement} trigger
 * @property {HTMLElement} panel
 *
 * @extends {Component<Refs>}
 */
class TooltipComponent extends Component {
  /**
   * Required refs for tooltip behavior.
   *
   * @type {string[]}
   */
  requiredRefs = ['trigger', 'panel'];

  /**
   * Cached viewport update handler for resize/scroll.
   * On touch devices the tooltip closes on scroll (standard mobile pattern) so
   * the shift variables never fight against page movement.
   *
   * @returns {void}
   */
  #handleViewportChange = () => {
    if (!this.isOpen) return;
    if (this.#isMobileInteraction()) return;
    this.#updatePosition();
  };

  /** @type {MediaQueryList | undefined} */
  #mobilePlacementQuery;

  /** @type {HTMLElement | null} */
  #overflowContainer = null;

  /**
   * Swaps the placement class to the mobile variant when the breakpoint matches.
   *
   * @returns {void}
   */
  #syncMobilePlacement = () => {
    const desktop = this.dataset.placement;
    const mobile = this.dataset.mobilePlacement;
    if (!desktop || !mobile) return;
    const useMobile = this.#mobilePlacementQuery?.matches ?? false;
    this.classList.toggle(`tooltip--${desktop}`, !useMobile);
    this.classList.toggle(`tooltip--${mobile}`, useMobile);
  };

  /**
   * Initializes ARIA state and outside-click listeners.
   *
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.refs.panel.hidden = true;
    this.#resetPositionVars();

    if (this.dataset.mobilePlacement) {
      this.#mobilePlacementQuery = window.matchMedia('(max-width: 749px)');
      this.#mobilePlacementQuery.addEventListener('change', this.#syncMobilePlacement);
      this.#syncMobilePlacement();
    }

    document.addEventListener('click', this.#handleDocumentClick, { capture: true });
    window.addEventListener('resize', this.#handleViewportChange);
    window.addEventListener('scroll', this.#handleViewportChange, true);
  }

  /**
   * Cleans up global listeners.
   *
   * @returns {void}
   */
  disconnectedCallback() {
    document.removeEventListener('click', this.#handleDocumentClick, { capture: true });
    window.removeEventListener('resize', this.#handleViewportChange);
    window.removeEventListener('scroll', this.#handleViewportChange, true);
    this.#mobilePlacementQuery?.removeEventListener('change', this.#syncMobilePlacement);
    this.#mobilePlacementQuery = undefined;
    super.disconnectedCallback();
  }

  /**
   * Toggles tooltip panel.
   *
   * @param {Event} event
   * @returns {void}
   */
  toggle = (event) => {
    event.preventDefault();
    const isKeyboardEvent = event instanceof KeyboardEvent;
    if (!isKeyboardEvent && !this.#isMobileInteraction()) return;
    this.isOpen ? this.close() : this.open();
  };

  /**
   * Opens tooltip panel.
   *
   * @returns {void}
   */
  open = () => {
    if (this.isOpen) return;
    // On touch devices, briefly suppress horizontal scrolling on body before
    // the panel enters the layout. This keeps window.scrollX stable so that
    // getBoundingClientRect() returns correct viewport-relative values for the
    // shift calculation. We use body (not documentElement) so the overflow
    // propagates to the viewport scroll container without creating a clip on
    // body's own box — absolutely-positioned descendants are never visually
    // clipped. Removed via requestAnimationFrame (before the first paint) so
    // sticky elements are not affected.
    const isMobile = this.#isMobileInteraction();
    if (isMobile) document.body.style.overflowX = 'hidden';
    this.refs.panel.hidden = false;
    this.#applyFallbackPlacement();
    this.#updatePosition();
    this.#adjustContainerOverflow();
    this.classList.add('is-open');
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    if (isMobile) requestAnimationFrame(() => { document.body.style.overflowX = ''; });
  };

  /**
   * Closes tooltip panel.
   *
   * @param {Event} [event]
   * @returns {void}
   */
  close = (event) => {
    event?.preventDefault();
    if (!this.isOpen) return;
    this.refs.panel.hidden = true;
    this.classList.remove('is-open');
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.#resetPositionVars();
    this.#restoreFallbackPlacement();
    this.#resetContainerOverflow();
    document.body.style.overflowX = '';
  };

  /**
   * Handles keyboard interactions on trigger.
   *
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  handleTriggerKeydown = (event) => {
    if (event.key === 'Escape') {
      this.close();
      this.refs.trigger.focus();
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.toggle(event);
  };

  /**
   * Closes tooltip on blur when focus leaves component.
   *
   * @param {FocusEvent} event
   * @returns {void}
   */
  handleBlur = (event) => {
    const relatedTarget = /** @type {Node | null} */ (event.relatedTarget);
    if (relatedTarget && this.contains(relatedTarget)) return;
    this.close();
  };

  /**
   * Opens tooltip on hover (hover-capable devices only).
   *
   * @returns {void}
   */
  handlePointerEnter = () => {
    if (this.#isMobileInteraction()) return;
    this.open();
  };

  /**
   * Closes tooltip when pointer leaves whole component (hover-capable devices only).
   *
   * @param {PointerEvent} event
   * @returns {void}
   */
  handlePointerLeave = (event) => {
    if (this.#isMobileInteraction()) return;
    const relatedTarget = /** @type {Node | null} */ (event.relatedTarget);
    if (relatedTarget && this.contains(relatedTarget)) return;
    this.close();
  };

  get isOpen() {
    return this.classList.contains('is-open');
  }

  /**
   * Closes tooltip when clicking outside of the component.
   *
   * @param {MouseEvent} event
   * @returns {void}
   */
  #handleDocumentClick = (event) => {
    if (!this.isOpen) return;
    if (!(event.target instanceof Node)) return;
    if (!this.contains(event.target)) {
      this.close();
    }
  };

  /**
   * Returns true for coarse pointer / non-hover devices.
   *
   * @returns {boolean}
   */
  #isMobileInteraction() {
    return window.matchMedia('(hover: none), (pointer: coarse)').matches;
  }

  /** Minimum side width in px before falling back to top placement. */
  static #MIN_SIDE_WIDTH = 160;

  /** Visible arrow protrusion in px (panel arrow is a 12px square rotated 45deg, offset -6px). */
  static #ARROW_SIZE = 6;

  /**
   * For left/right placements checks if there is enough horizontal space.
   * If not, temporarily switches to top placement.
   *
   * @returns {void}
   */
  #applyFallbackPlacement() {
    const placement = this.#getActivePlacement();
    if (placement !== 'left' && placement !== 'right') return;
    if (this.classList.contains('tooltip--placement-fallback')) return;

    const padding = this.#getViewportPadding();
    const triggerRect = this.refs.trigger.getBoundingClientRect();
    const offset = this.#getPanelOffset();
    const arrow = TooltipComponent.#ARROW_SIZE;
    const available =
      placement === 'right'
        ? window.innerWidth - padding - triggerRect.right - offset - arrow
        : triggerRect.left - padding - offset - arrow;

    const panelWidth = this.refs.panel.offsetWidth;

    if (panelWidth <= available) return;

    if (!this.#isMobileInteraction() && available >= TooltipComponent.#MIN_SIDE_WIDTH) {
      this.refs.panel.style.maxWidth = `${Math.floor(available)}px`;
      this.classList.add('tooltip--panel-narrowed');
    } else {
      this.classList.remove(`tooltip--${placement}`);
      this.classList.add('tooltip--top', 'tooltip--placement-fallback');
    }
  }

  /**
   * Restores the original placement class if a fallback was applied.
   *
   * @returns {void}
   */
  #restoreFallbackPlacement() {
    if (this.classList.contains('tooltip--panel-narrowed')) {
      this.refs.panel.style.maxWidth = '';
      this.classList.remove('tooltip--panel-narrowed');
    }
    if (!this.classList.contains('tooltip--placement-fallback')) return;
    const placement = this.#getActivePlacement();
    this.classList.remove('tooltip--top', 'tooltip--placement-fallback');
    if (placement) this.classList.add(`tooltip--${placement}`);
  }

  /**
   * Returns the currently active placement, accounting for the mobile override.
   *
   * @returns {string | undefined}
   */
  #getActivePlacement() {
    const desktop = this.dataset.placement;
    const mobile = this.dataset.mobilePlacement;
    if (mobile && this.#mobilePlacementQuery?.matches) return mobile;
    return desktop;
  }

  /**
   * Calculates viewport overflow and writes correction offsets to CSS variables.
   *
   * @returns {void}
   */
  #updatePosition() {
    const panel = this.refs.panel;
    this.#resetPositionVars();

    const rect = panel.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const viewportPadding = this.#getViewportPadding();

    let shiftX = 0;
    let shiftY = 0;

    if (rect.left < viewportPadding) {
      shiftX += viewportPadding - rect.left;
    }

    if (rect.right > viewportWidth - viewportPadding) {
      shiftX -= rect.right - (viewportWidth - viewportPadding);
    }

    if (rect.top < viewportPadding) {
      shiftY += viewportPadding - rect.top;
    }

    if (rect.bottom > viewportHeight - viewportPadding) {
      shiftY -= rect.bottom - (viewportHeight - viewportPadding);
    }

    panel.style.setProperty('--tooltip-shift-x', `${Math.round(shiftX)}px`);
    panel.style.setProperty('--tooltip-shift-y', `${Math.round(shiftY)}px`);
    panel.style.setProperty('--tooltip-arrow-shift-x', `${Math.round(-shiftX)}px`);
  }

  /**
   * Resets tooltip positioning CSS variables.
   *
   * @returns {void}
   */
  #resetPositionVars() {
    const panel = this.refs.panel;
    panel.style.setProperty('--tooltip-shift-x', '0px');
    panel.style.setProperty('--tooltip-shift-y', '0px');
    panel.style.setProperty('--tooltip-arrow-shift-x', '0px');
  }

  /**
   * Walks up the DOM and returns the nearest ancestor whose overflow clips
   * content (overflow: hidden or clip on either axis).
   *
   * @returns {HTMLElement | null}
   */
  #findOverflowContainer() {
    let el = this.parentElement;
    while (el && el !== document.documentElement) {
      const { overflow, overflowY } = getComputedStyle(el);
      if (overflow === 'hidden' || overflow === 'clip' || overflowY === 'hidden' || overflowY === 'clip') {
        return /** @type {HTMLElement} */ (el);
      }
      el = el.parentElement;
    }
    return null;
  }

  /**
   * If the panel overflows below the nearest clipping ancestor, expands that
   * ancestor's --tooltip-overflow-extra variable to make room.
   * getBoundingClientRect() is called after #updatePosition() so the
   * transform shift is already reflected in the returned rect.
   *
   * @returns {void}
   */
  #adjustContainerOverflow() {
    const placement = this.#getActivePlacement();
    if (!placement || placement === 'top') return;

    const container = this.#findOverflowContainer();
    if (!container) return;

    const panelRect = this.refs.panel.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const overflowBelow = panelRect.bottom - containerRect.bottom;
    if (overflowBelow <= 0) return;

    const currentPadding = parseFloat(getComputedStyle(container).paddingBottom) || 0;
    const needed = Math.ceil(currentPadding + overflowBelow + 16);
    container.style.setProperty('--tooltip-overflow-extra', `${needed}px`);
    this.#overflowContainer = container;
  }

  /**
   * Removes the dynamic padding adjustment set by #adjustContainerOverflow.
   *
   * @returns {void}
   */
  #resetContainerOverflow() {
    if (!this.#overflowContainer) return;
    this.#overflowContainer.style.removeProperty('--tooltip-overflow-extra');
    this.#overflowContainer = null;
  }

  /**
   * Reads viewport safe padding from CSS variable.
   *
   * @returns {number}
   */
  #getViewportPadding() {
    const value = getComputedStyle(this).getPropertyValue('--tooltip-viewport-padding').trim();
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 16;
  }

  /**
   * Reads the panel offset (gap between trigger and panel) from CSS variable.
   *
   * @returns {number}
   */
  #getPanelOffset() {
    const value = getComputedStyle(this).getPropertyValue('--tooltip-offset-y').trim();
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
}

if (!customElements.get('tooltip-component')) {
  customElements.define('tooltip-component', TooltipComponent);
}
